import { cn } from '@myosh/odin-components';
import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { memo, useMemo } from 'react';
import { makeDataCy } from '../../../helpers/util';

export type BaseContainerNodeData = {
  label: string;
  className?: string;
};
type BaseContainerNodeType = Node<BaseContainerNodeData>;
type BaseContainerNodeProps = NodeProps<BaseContainerNodeType>;

/**
 * BaseContainerNode component.
 *
 * This component represents a base container node used in the flow.
 * It renders a div with a dashed border and optional label.
 *
 * @component
 * @param {BaseContainerNodeProps} props - The properties for the BaseContainerNode component.
 * @param {BaseContainerNodeType} props.data - The data for the node.
 * @param {string} [props.data.label] - Optional label to be displayed inside the node.
 * @param {string} [props.data.className] - Optional additional class names to be applied to the node.
 * @returns {JSX.Element} The rendered BaseContainerNode component.
 *
 * @example
 * ```tsx
 * <BaseContainerNode data={{ label: 'Node Label', className: 'custom-class' }} />
 * ```
 */
const BaseContainerNode = ({ id, data }: BaseContainerNodeProps): React.JSX.Element => {
  const { label, className } = data;
  const dataCyLabel = useMemo(() => makeDataCy(label), [label]);

  return (
    <div
      id={id}
      data-cy={`base-container-node-${dataCyLabel}`}
      className={cn(
        'nopan nodrag flex h-full w-full justify-center rounded-md border-2 border-dashed px-2 py-4',
        className
      )}
    >
      {label && <div className="text-base font-bold">{label}</div>}
    </div>
  );
};

export default memo(BaseContainerNode);
